import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import moment from 'moment';
import memoizeOne from 'memoize-one';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { Refresh as RefreshIcon, Add as AddIcon, RemoveRedEye as ViewIcon, FindReplace as MoreIcon } from '@material-ui/icons';
import { FHETable } from 'data/components';
import { Toastr } from 'data/common';

import { getAllIssues } from '../../data/Issues/actions';
import { getAllProjects } from '../../data/Projects/actions';

class Issue extends Component {
    static propTypes = {
        getAllProjects: PropTypes.func.isRequired,
        getAllIssues: PropTypes.func.isRequired,
        nextPage: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
        environment: PropTypes.string.isRequired,
        projects: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        profile: PropTypes.object.isRequired,
        issues: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        const { totalPages } = props;
        this.state = {
            loading: true,
            showLoadMoreBtn: totalPages > 1
        };
    }

    componentDidMount() {
        this.getAllData();
    }

    getAllData = () => {
        this.setState({ loading: true });
        const promises = [this.getAllProjects(), this.getAllIssues()];
        Promise.all(promises)
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    getAllProjects = () => {
        const { getAllProjects } = this.props;

        return getAllProjects().catch(err => {
            Toastr.error(Translator.translate('ticketsgit.issues.toast.error.projects'));
            throw err;
        });
    };

    getAllIssues = async () => {
        const { currentPage } = this.props;

        return new Promise(async (resolve, reject) => {
            const getIssues = async (offset = 1) => {
                await this.getOnePageOfIssues(offset).catch(err => {
                    Toastr.error(Translator.translate('ticketsgit.issues.toast.error.issues'));
                    reject(err);
                });
                offset = offset + 1;
                if (offset <= currentPage) return getIssues(offset);
            };
            await getIssues();
            resolve();
        });
    };

    getOnePageOfIssues = offset => {
        const { getAllIssues } = this.props;

        return getAllIssues(offset);
    };

    navigateToDetail = rowData => {
        const { history } = this.props;
        return history.push({ pathname: `issues/${rowData.issueIid}/projects/${rowData.project_id}` });
    };

    navigateToAdd = () => {
        const { history } = this.props;
        return history.push({ pathname: 'issues/create' });
    };

    handleBtnloadMore = () => {
        const { nextPage } = this.props;

        this.setState({ loading: true });
        return this.getOnePageOfIssues(nextPage)
            .then(pages => {
                const newState = { loading: false };
                if (pages.current >= pages.total) newState.showLoadMoreBtn = false;
                this.setState(newState);
            })
            .catch(err => {
                Toastr.error(Translator.translate('ticketsgit.issues.toast.error.issues'));
                this.setState({ loading: false });
                throw err;
            });
    };

    renderActions = memoizeOne((profile, showLoadMoreBtn) => {
        const { environment } = this.props;
        const userRights = profile.getIn(['user_rights', environment], []);

        let actions = {
            row: [],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate('ticketsgit.issues.table.globalAction.refresh.tooltip'),
                    onClick: this.getAllData
                }
            ]
        };
        if (profile.get('admin') || userRights.includes('issueCreate')) {
            actions.toolbar.push({
                icon: AddIcon,
                tooltip: Translator.translate('ticketsgit.issues.table.globalAction.add.tooltip'),
                onClick: this.navigateToAdd
            });
        }
        if (showLoadMoreBtn) {
            actions.toolbar.push({
                icon: MoreIcon,
                tooltip: Translator.translate('ticketsgit.issues.actions.loadMore'),
                onClick: this.handleBtnloadMore
            });
        }
        if (profile.get('admin') || userRights.includes('issueDetails')) {
            actions.row.push({
                icon: ViewIcon,
                tooltip: Translator.translate('general.showDetail'),
                onClick: this.navigateToDetail
            });
        }
        return actions;
    });

    renderColumns = memoizeOne(profile => {
        const { environment } = this.props;
        const userRights = profile.getIn(['user_rights', environment], []);

        return [
            {
                title: Translator.translate('general.table.column.idIid'),
                name: 'id'
            },
            {
                title: Translator.translate('general.table.column.title'),
                name: 'title',
                action: (profile.get('admin') || userRights.includes('issueDetails')) && {
                    onClick: this.navigateToDetail,
                    tooltip: Translator.translate('general.showDetail')
                }
            },
            {
                title: Translator.translate('general.table.column.created_at'),
                name: 'created_at',
                defaultSort: 'desc'
            },
            {
                title: Translator.translate('general.table.column.issueState'),
                name: 'state',
                grouped: 1,
                defaultSort: 'desc'
            }
        ];
    });

    renderData = memoizeOne((projects, issues) => {
        let results = [];
        issues.forEach((issue, id) => {
            const project = projects.find((project, projectId) => parseInt(projectId) === issue.get('project_id'), null, new Map());
            results.push({
                id: `${id} / ${issue.get('iid')}`,
                issueId: id,
                issueIid: issue.get('iid'),
                title: issue.get('title'),
                project_id: issue.get('project_id'),
                created_at: moment(issue.get('created_at')).format('DD/MM/YYYY HH:mm:ss'),
                sort_created_at: issue.get('created_at'),
                projectName: project.getIn(['name'], ''),
                state: Translator.translate(`ticketsgit.issues.table.state.${issue.get('state')}`)
            });
        });
        return results;
    });

    render() {
        const { profile, projects, issues } = this.props;
        const { loading, showLoadMoreBtn } = this.state;

        return (
            <FHETable
                idTable="issuesList"
                columns={this.renderColumns(profile)}
                data={this.renderData(projects, issues)}
                actions={this.renderActions(profile, showLoadMoreBtn)}
                title={Translator.translate('ticketsgit.issues.table.title')}
                emptyDataSourceMessage={Translator.translate('ticketsgit.issues.table.body.emptyDataSourceMessage')}
                isLoading={loading}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const { environment } = props;

    return {
        nextPage: state.environments[environment].data.Issues.get('nextPage'),
        currentPage: state.environments[environment].data.Issues.get('currentPage'),
        totalPages: state.environments[environment].data.Issues.get('totalPages'),
        projects: state.environments[environment].data.Projects.get('list'),
        issues: state.environments[environment].data.Issues.get('list'),
        profile: state.data.user
    };
}

const styles = () => ({
    colorWhite: {
        color: '#EBEBEB'
    },
    container: {
        padding: '0 15px'
    },
    containerButtons: {
        display: 'flex'
    }
});

export default withRouter(
    connect(
        mapStateToProps,
        dispatch => bindActionCreators({ getAllProjects, getAllIssues }, dispatch)
    )(withStyles(styles, { withTheme: true })(Issue))
);



// WEBPACK FOOTER //
// ./environments/fhe/scenes/Issues/index.js